import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpService } from 'src/app/services/http.service';
import { LanguageService } from 'src/app/services/language.service';
import { SettingsService } from 'src/app/services/settings.service';
import { UtilsService } from 'src/app/services/utils.service';
import { ListFields } from '../listfields';
import { BikeElement } from './bike';
import { forkJoin } from 'rxjs';
import { DatePipe } from '@angular/common';

/** List view of the bikes */

@Component({
  selector: 'app-manage-bikes',
  templateUrl: './manage-bikes.component.html',
  styleUrls: ['./manage-bikes.component.scss'],
  standalone: false
})
export class ManageBikesComponent implements OnInit {

  @ViewChild('columnList') columnListElementRef: any;

  /** All of the fields that possibly can be visible */
  public manageAllBikesFields: ListFields[] = [
    { Id: 0, FieldName: "BikeSerial", Uppercase: true, DisplayName: this.languageService.languageJSON.ManageBikes_BikeID, SearchBy: true, GroupBy: true, SearchName: "bike_id" },
    { Id: 1, FieldName: "ConfigId", Uppercase: false, DisplayName: this.languageService.languageJSON.ManageBikes_ConfigID, SearchBy: true, GroupBy: true, SearchName: "config_id" },
    { Id: 2, FieldName: "OEMName", Uppercase: false, DisplayName: this.languageService.languageJSON.ComponentDetails_OEMName, SearchBy: true, GroupBy: true, SearchName: "oem_name" },
    { Id: 3, FieldName: "ConfigName", Uppercase: false, DisplayName: this.languageService.languageJSON.ManageBikes_ConfigName, SearchBy: true, GroupBy: true, SearchName: "config_name" },
    { Id: 4, FieldName: "ApprovalBikeString", Uppercase: false, DisplayName: this.languageService.languageJSON.ManageBikes_ApprovalBike, SearchBy: true, GroupBy: true, SearchName: "approval", BooleanSelector: true },
    { Id: 5, FieldName: "Brand", Uppercase: false, DisplayName: this.languageService.languageJSON.Global_BikeBrand, SearchBy: true, GroupBy: true, SearchName: "brand" },
    { Id: 6, FieldName: "BikeModelName", Uppercase: false, DisplayName: this.languageService.languageJSON.ManageBikes_BikeModelName, SearchBy: true, GroupBy: true, SearchName: "bike_model_name" },
    { Id: 7, FieldName: "EOLDate", Uppercase: false, DisplayName: this.languageService.languageJSON.Bike_EolDate, SearchBy: true, GroupBy: true, SearchName: "eol_date", DateInterval: true },
    { Id: 8, FieldName: "BikeType", Uppercase: false, DisplayName: this.languageService.languageJSON.ComponentDetails_BikeType, SearchBy: true, GroupBy: true, SearchName: "bike_type" },
    { Id: 9, FieldName: "FrameId", Uppercase: false, DisplayName: this.languageService.languageJSON.ComponentDetails_FrameNumber, SearchBy: true, GroupBy: true, SearchName: "frame_id" }
  ];

  public customizeDropdownVisible: boolean = false;
  public bikeList: BikeElement[] = [];
  public searchByList: any = {};
  public loading: boolean = true;
  public downloadingCSV: boolean = false;
  public order: string = this.manageAllBikesFields[0].FieldName;
  public reverse: boolean = false;
  public caseInsensitive: boolean = true;
  public manageBikes: boolean = true;

  public page: number = 1;
  public totalItems: number = 0;
  public itemsPerPage: number = this.settingsService.getItemsPerPage();
  public paginationValues: number[] = this.settingsService.getPaginationValues();
  public bikesTitle: string = "";

  private rolloutId: number = -1;
  private approvalRolloutId: number = -1;
  private softwareBikeType: string = "";
  private bikeTypeOptionsList: { id: number, name: string }[] = [];

  public selectedColumnsGroup: FormGroup = this.fb.group({
    selectedColumns: new FormControl([]),
  });

  get manageBikesFields(): ListFields[] {
    let manageBikesFieldsArray: ListFields[] = [];
    this.selectedColumns?.value.forEach((el: number) => {
      manageBikesFieldsArray.push(this.manageAllBikesFields[el]);
    });
    return manageBikesFieldsArray;
  }

  get selectedColumns() {
    return this.selectedColumnsGroup.get("selectedColumns");
  }

  constructor(public languageService: LanguageService, private httpService: HttpService,
    private router: Router, private settingsService: SettingsService, private utilsService: UtilsService,
    private fb: FormBuilder, private route: ActivatedRoute, private datePipe: DatePipe) { }

  /** Initializing bikes*/
  ngOnInit(): void {
    const url = window.location.href;
    this.selectedColumns?.setValue([0, 1, 2, 3, 4, 5]);
    if (localStorage.getItem("manageBikesSelectedColumns")) {
      this.selectedColumns?.setValue(JSON.parse(localStorage.getItem("manageBikesSelectedColumns")!));
    }
    if (url.includes("bike-overview")) {
      this.initManageBikes();
    }
    else {
      this.manageBikes = false;
      this.route.queryParams
        .subscribe(params => {
          this.rolloutId = params['rolloutId'];
          this.approvalRolloutId = params['approval'];
        });
      if (url.includes("software-bikes-details")) {
        this.softwareBikeType = "bikes";
      }
      if (url.includes("software-deliveries-details")) {
        this.softwareBikeType = "deliveries";
      }
      if (url.includes("software-success-details")) {
        this.softwareBikeType = "success";
      }
      if (url.includes("software-errors-details")) {
        this.softwareBikeType = "errors";
      }
      this.initSoftwareBikes(this.softwareBikeType);
    }
  }

  public initManageBikes() {
    this.bikesTitle = this.languageService.languageJSON.ManageBikes_Title;
    if (localStorage.getItem("manageBikesRows")) {
      this.itemsPerPage = parseInt(localStorage.getItem("manageBikesRows") || "50");
    }

    this.route.queryParams
      .subscribe(params => {
        if (params['configurationId']) {
          this.searchByList = { ConfigId: params['configurationId'] };
        }
        this.initManageBikesList();
      });
  }

  public initSoftwareBikes(type: string) {
    this.loading = true;
    if (type == "bikes") {
      this.bikesTitle = this.languageService.languageJSON.ManageBikes_BikesDetails.replace("$rolloutName", "R" + this.rolloutId);
    }
    if (type == "deliveries") {
      this.bikesTitle = this.languageService.languageJSON.ManageBikes_DeliveriesDetails.replace("$rolloutName", "R" + this.rolloutId);
    }
    if (type == "success") {
      this.bikesTitle = this.languageService.languageJSON.ManageBikes_SuccessDetails.replace("$rolloutName", "R" + this.rolloutId);
    }
    if (type == "errors") {
      this.bikesTitle = this.languageService.languageJSON.ManageBikes_ErrorsDetails.replace("$rolloutName", "R" + this.rolloutId);
    }

    this.bikeList = [];

    forkJoin([this.httpService.getSoftwareBikeDetails(this.rolloutId, this.approvalRolloutId, type, this.itemsPerPage, this.page, this.getSearchValue(),
      this.reverse ? "desc" : "asc", this.getOrderBackendName(this.order)),
    this.httpService.getBikeTypeOptionsList(),
    this.httpService.getOEOptionsList(1, 0)])
      .subscribe((resp: any) => {
        this.bikeTypeOptionsList = resp[1];
        if (resp[0].success) {
          this.bikeList = resp[0].data.map((el: any) => {
            const bikeElement: BikeElement = {
              BikeId: el.id,
              BikeSerial: el.serial_id,
              ConfigId: el.configuration_id,
              EOLDate: this.datePipe.transform(new Date(el.eol_date), "long") || "",
              Brand: el.configuration.brand.name,
              BikeModelName: el.configuration.bike_model_name,
              ConfigName: el.configuration.config_name,
              ApprovalBike: el.approval,
              ApprovalBikeString: el.approval ? this.languageService.languageJSON.Global_Yes : this.languageService.languageJSON.Global_No,
              ComponentsHistories: [],//el.components_histories.map((component: any) => { return { Component: component.component } }),
              BikeHistories: [],//el.bike_histories.map((bike: any) => { return { Bike: bike.bike } }),
              BikeType: this.getBikeTypeNameById(el.configuration.motor_configuration_id),
              OEMName: this.utilsService.getOEMName(el.configuration.oe, resp[2]),
              FrameId: el.frame_id
            }

            return bikeElement;
          });

          this.totalItems = resp[0].records ? resp[0].records : 0;
        }
        else {
          this.totalItems = 0;
        }
        this.loading = false;
      });
  }

  public hasAnySearchElement(): boolean {
    return Object.keys(this.searchByList).length != 0;
  }

  public getValue(bikeElement: any, key: any): string {
    return bikeElement[key];
  }

  /** Changing table ordering by selected field */
  setOrder(value: string, groupBy: boolean) {
    if (groupBy) {
      if (this.order === value) {
        this.reverse = !this.reverse;
      }

      this.order = value;
    }

    this.reload();
  }

  /** Getting bike list from Api endpoint */
  private initManageBikesList(): void {
    this.loading = true;

    const postData: any = {
      order: this.getOrderBackendName(this.order),
      direction: this.reverse ? "desc" : "asc",
      page: this.page,
      recordsPerPage: this.itemsPerPage
    };

    if (Object.keys(this.searchByList).length) {
      postData.search = this.getSearchValue();
    }

    forkJoin([this.httpService.getBikeOverviewList(postData),
    this.httpService.getBikeTypeOptionsList(),
    this.httpService.getOEOptionsList(1, 0)]).subscribe((resp: any) => {
      this.loading = false;
      this.bikeTypeOptionsList = resp[1];
      if (resp[0].success) {
        this.bikeList = resp[0].data.filter((el: any) => { return el.unassigned != 1 }).map((el: any) => {
          const bikeElement: BikeElement = {
            BikeId: el.id,
            BikeSerial: el.serial_id,
            ConfigId: el.configuration_id,
            EOLDate: this.datePipe.transform(new Date(el.eol_date), "long") || "",
            Brand: el.configuration.brand.name,
            BikeModelName: el.configuration.bike_model_name,
            ConfigName: el.configuration.config_name,
            ApprovalBike: el.approval,
            ApprovalBikeString: el.approval ? this.languageService.languageJSON.Global_Yes : this.languageService.languageJSON.Global_No,
            ComponentsHistories: [],//el.components_histories.map((component: any) => { return { Component: component.component } }),
            BikeHistories: [],//el.bike_histories.map((bike: any) => { return { Bike: bike.bike } }),
            BikeType: this.getBikeTypeNameById(el.configuration.motor_configuration_id),
            OEMName: this.utilsService.getOEMName(el.configuration.oe, resp[2]),
            FrameId: el.frame_id
          };

          return bikeElement;
        });
        this.totalItems = resp[0].records ? resp[0].records : 0;
      }
      else if (resp[0].failed?.code == "E001") {
        this.bikeList = [];
        this.totalItems = 0;
      }
    }, (error) => {
      this.loading = false;
    });
  }

  private getSearchValue() {
    const searchListBackend: any = {};
    for (const [key, value] of Object.entries(this.searchByList)) {
      const searchKey = this.manageBikesFields.find((formFieldEl) => { return formFieldEl.FieldName == key })?.SearchName;
      if (searchKey != undefined) {
        if (searchKey == "eol_date") {
          const startEndDateArray = (value as string).split(' - ');
          searchListBackend.start_date = startEndDateArray[0].replaceAll("/", "-") + " 00:00:00";
          searchListBackend.end_date = startEndDateArray[1].replaceAll("/", "-") + " 23:59:59";
        }
        else {
          searchListBackend[searchKey] = value;
        }
      }
    }
    return searchListBackend;
  }

  private getOrderBackendName(order: string) {
    if (order == "BikeSerial") {
      return "bike_id";
    }
    if (order == "ConfigId") {
      return "config_id";
    }
    if (order == "ConfigName") {
      return "config_name";
    }
    if (order == "ApprovalBikeString") {
      return "approval";
    }
    if (order == "Brand") {
      return "brand";
    }
    if (order == "BikeModelName") {
      return "bike_model_name";
    }
    if (order == "EOLDate") {
      return "eol_date";
    }
    if (order = "BikeType") {
      return "bike_type";
    }
    if (order = "FrameId") {
      return "frame_id";
    }

    return "bike_id";
  }

  /** Returns bike type name by id */
  private getBikeTypeNameById(id: number): string {
    return this.bikeTypeOptionsList.find((el) => { return el.id == id })?.name as string;
  }

  public columnsSelectionChanged() {
    let isReload = false;
    localStorage.setItem("manageBikesSelectedColumns", JSON.stringify(this.selectedColumns?.value));
    for (let i = 0; i < this.manageAllBikesFields.length; i++) {
      if (!this.selectedColumns?.value.includes(i) && this.searchByList[this.manageAllBikesFields[i].FieldName!]) {
        delete this.searchByList[this.manageAllBikesFields[i].FieldName!];
        isReload = true;
      }

    }
    if (isReload) {
      this.reload();
    }
  }

  public onSearchListChange(newSearchList: any): void {
    this.page = 1;
    this.searchByList = newSearchList;
    this.reload();
  }

  /** Opens view bike details page */
  public viewBike(bike: BikeElement): void {
    this.router.navigate(['/bike-details'], { queryParams: { serial: bike.BikeId } });
  }

  /** Download bike overview table in CSV */
  downloadCSV() {
    this.downloadingCSV = true;

    if (this.softwareBikeType == "") {
      this.httpService.getBikeOverviewListCSV().subscribe((resp) => {
        this.downloadingCSV = false;
        this.utilsService.downloadCSVFile(resp, "bike-overview");
      }, () => { this.downloadingCSV = false });
    }
    else if (this.softwareBikeType == "bikes") {
      let postData: { approval_rollout_id?: number, rollout_id?: number } = {};
      if (this.rolloutId) {
        postData.rollout_id = this.rolloutId;
      }
      if (this.approvalRolloutId) {
        postData.approval_rollout_id = this.approvalRolloutId;
      }
      this.httpService.getSoftwareBikesListCSV(postData).subscribe((resp) => {
        this.downloadingCSV = false;
        this.utilsService.downloadCSVFile(resp, "bike-overview");
      }, () => { this.downloadingCSV = false });
    }
  }

  public itemsPerPageChanged(): void {
    this.page = 1;
    this.reload();
    localStorage.setItem("manageBikesRows", this.itemsPerPage.toString());
  }

  public getPaginationText(): string {
    return this.utilsService.getPaginationText(this.itemsPerPage, this.page, this.totalItems);
  }

  public onPageChangeEvent(event: number) {
    this.page = event;
    this.reload();
  }

  private reload() {
    if (this.rolloutId == -1) {
      this.initManageBikesList();
    }
    else {
      this.initSoftwareBikes(this.softwareBikeType);
    }
  }
}
